import { GenericAbortSignal } from 'axios';

import http from 'services/http';

// A recorded portal-retrieval script. The index serves light rows; show
// adds the replayable payload (entryUrl + body) the browser extension executes.
export interface PortalScriptOptions {
  id: string;
  name: string;
  sourceSystem: string;
  createdAt: string;
  updatedAt: string;
}

export interface PortalScriptDetailOptions extends PortalScriptOptions {
  entryUrl: string;
  body: string;
  retrievalUrl: string | null;
}

export const portalScriptQueryKeys = {
  base: ['portalScripts'] as const,
  list: (sourceSystem: string) => ['portalScripts', 'list', sourceSystem] as const,
  show: (id: string) => ['portalScripts', 'show', id] as const,
};

export async function indexPortalScripts(sourceSystem: string, signal?: GenericAbortSignal) {
  return http
    .get<{ data: PortalScriptOptions[] }>(`portal_scripts`, { params: { source_system: sourceSystem }, signal })
    .then((res) => res.data.data);
}

export async function showPortalScript(id: string, signal?: GenericAbortSignal) {
  return http.get<PortalScriptDetailOptions>(`portal_scripts/${id}`, { signal }).then((res) => res.data);
}

// ---- Admin surface (admin → Clients → EditClient → Portal Automation) ----
// Every user's scripts for a client, light rows only — bodies are owner-only
// and never reach the admin surface. Management mirrors the owner surface:
// rename and archive/restore.

export interface AdminPortalScript extends PortalScriptOptions {
  archivedAt: string | null;
  user: {
    id: string;
    name: string;
    email: string;
  };
}

export const adminPortalScriptQueryKeys = {
  base: ['adminPortalScripts'] as const,
  list: (clientId: string) => ['adminPortalScripts', 'list', clientId] as const,
};

export async function indexAdminPortalScripts(clientId: string, signal?: GenericAbortSignal) {
  return http
    .get<{ data: AdminPortalScript[] }>('admin/portal_scripts', { params: { client_id: clientId }, signal })
    .then((res) => res.data.data);
}

export type UpdateAdminPortalScriptInput = {
  id: string;
  name?: string;
  archived?: boolean;
};

export async function updateAdminPortalScript({ id, ...patch }: UpdateAdminPortalScriptInput) {
  return http.put<AdminPortalScript>(`admin/portal_scripts/${id}`, patch).then((res) => res.data);
}
